import { useState, useRef, useEffect, ChangeEvent, FormEvent } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/d7l7ue3vbq3afd5663hg';

const tiposConsorcio = [
  { value: 'imovel', label: 'Imóvel', icon: 'ri-home-4-line' },
  { value: 'automovel', label: 'Automóvel', icon: 'ri-car-line' },
  { value: 'moto', label: 'Moto', icon: 'ri-motorbike-line' },
  { value: 'construcao', label: 'Construção', icon: 'ri-building-line' },
  { value: 'outros', label: 'Outros', icon: 'ri-more-line' },
];

function formatCurrency(value: string): string {
  const digits = value.replace(/\D/g, '');
  if (!digits) return '';
  const num = parseInt(digits, 10) / 100;
  return num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function parseCurrency(value: string): number {
  const digits = value.replace(/\D/g, '');
  if (!digits) return 0;
  return parseInt(digits, 10) / 100;
}

function formatPhone(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 2) return `(${digits}`;
  if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 11) return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  return value;
}

export default function Simulador() {
  const [form, setForm] = useState({
    nome: '',
    telefone: '',
    tipo: '',
    valorPago: '',
  });
  const [valorEstimado, setValorEstimado] = useState<number | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const resultRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            (entry.target as HTMLElement).style.opacity = '1';
            (entry.target as HTMLElement).style.transform = 'translateY(0)';
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === 'telefone') {
      setForm((prev) => ({ ...prev, telefone: formatPhone(value) }));
    } else if (name === 'valorPago') {
      const formatted = formatCurrency(value);
      setForm((prev) => ({ ...prev, valorPago: formatted }));
      const num = parseCurrency(value);
      if (num > 0) {
        setValorEstimado(num * 0.76);
      } else {
        setValorEstimado(null);
      }
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!form.nome.trim()) newErrors.nome = 'Informe seu nome completo.';
    if (form.telefone.replace(/\D/g, '').length < 10) newErrors.telefone = 'Telefone inválido.';
    if (!form.tipo) newErrors.tipo = 'Selecione o tipo de consórcio.';
    if (parseCurrency(form.valorPago) < 10000) newErrors.valorPago = 'Valor mínimo de R$ 10.000,00.';
    return newErrors;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setLoading(true);
    try {
      const body = new URLSearchParams({
        nome: form.nome,
        email: '',
        telefone: form.telefone,
        tipo_consorcio: tiposConsorcio.find((t) => t.value === form.tipo)?.label || form.tipo,
        valor_pago: `R$ ${form.valorPago}`,
        valor_estimado: `R$ ${(valorEstimado ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
      });
      
      await fetch('https://reconsorcio.com.br/api/enviar-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nome: form.nome,
          telefone: form.telefone,
          tipo: tiposConsorcio.find((t) => t.value === form.tipo)?.label,
          valorPago: form.valorPago,
          valorEstimado: valorEstimado,
        }),
      });;
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="simulador" className="py-20 md:py-28 bg-gradient-to-br from-green-800 to-emerald-700 relative overflow-hidden">
      <div className="absolute top-0 right-0 w-80 h-80 bg-green-600/30 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-emerald-500/20 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>

      <div
        ref={sectionRef}
        className="relative z-10 max-w-2xl mx-auto px-4 md:px-8"
        style={{ opacity: 0, transform: 'translateY(32px)', transition: 'opacity 0.7s ease, transform 0.7s ease' }}
      >
        <div className="text-center mb-10">
          <span className="inline-block bg-green-400/20 border border-green-400/30 text-green-200 text-sm font-medium px-4 py-1.5 rounded-full mb-4">
            Simulação Gratuita
          </span>
          <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-3">
            Simule Quanto Você Pode Recuperar
          </h2>
          <p className="text-green-200/80 text-base">
            Preencha os dados abaixo em menos de 1 minuto
          </p>
        </div>

        {submitted ? (
          <div className="bg-white rounded-3xl p-10 text-center">
            <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
              <i className="ri-check-double-line text-green-600 text-3xl"></i>
            </div>
            <h3 className="text-2xl font-extrabold text-gray-900 mb-2">Simulação Enviada!</h3>
            <p className="text-gray-500 mb-6">
              Nossa equipe entrará em contato em até <strong>24 horas</strong> com a análise completa do seu caso.
            </p>
            {valorEstimado !== null && (
              <div className="bg-green-50 border border-green-200 rounded-2xl p-5 mb-6">
                <p className="text-sm text-gray-500 mb-1">Você pode recuperar até:</p>
                <p className="text-4xl font-extrabold text-green-600">
                  R$ {valorEstimado.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </p>
              </div>
            )}
            <a
              href={`https://wa.me/5511998881316?text=${encodeURIComponent('Olá, fiz uma simulação no site e quero saber mais sobre a restituição do meu consórcio.')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white font-bold px-8 py-4 rounded-full transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-whatsapp-line text-xl"></i>
              Falar com especialista no WhatsApp
            </a>
          </div>
        ) : (
          <form
            data-readdy-form
            id="form-simulacao"
            onSubmit={handleSubmit}
            className="bg-white rounded-3xl p-6 md:p-10 space-y-5"
          >
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                <i className="ri-user-line mr-1 text-green-500"></i> Nome completo
              </label>
              <input
                type="text"
                name="nome"
                value={form.nome}
                onChange={handleChange}
                placeholder="Seu nome completo"
                className={`w-full h-14 px-4 rounded-xl border text-sm text-gray-800 outline-none transition-all ${errors.nome ? 'border-red-400 bg-red-50' : 'border-gray-200 focus:border-green-400 bg-gray-50'}`}
              />
              {errors.nome && <p className="text-red-500 text-xs mt-1">{errors.nome}</p>}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                <i className="ri-phone-line mr-1 text-green-500"></i> Telefone / WhatsApp
              </label>
              <input
                type="tel"
                name="telefone"
                value={form.telefone}
                onChange={handleChange}
                placeholder="(00) 00000-0000"
                className={`w-full h-14 px-4 rounded-xl border text-sm text-gray-800 outline-none transition-all ${errors.telefone ? 'border-red-400 bg-red-50' : 'border-gray-200 focus:border-green-400 bg-gray-50'}`}
              />
              {errors.telefone && <p className="text-red-500 text-xs mt-1">{errors.telefone}</p>}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                <i className="ri-list-check-2 mr-1 text-green-500"></i> Tipo de consórcio
              </label>
              <select
                name="tipo"
                value={form.tipo}
                onChange={handleChange}
                className={`w-full h-14 px-4 rounded-xl border text-sm text-gray-800 outline-none transition-all cursor-pointer ${errors.tipo ? 'border-red-400 bg-red-50' : 'border-gray-200 focus:border-green-400 bg-gray-50'}`}
              >
                <option value="">Selecione o tipo</option>
                {tiposConsorcio.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
              {errors.tipo && <p className="text-red-500 text-xs mt-1">{errors.tipo}</p>}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                <i className="ri-money-dollar-circle-line mr-1 text-green-500"></i> Valor total pago (R$)
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-medium">R$</span>
                <input
                  type="text"
                  name="valorPago"
                  value={form.valorPago}
                  onChange={handleChange}
                  placeholder="0,00"
                  className={`w-full h-14 pl-10 pr-4 rounded-xl border text-sm text-gray-800 outline-none transition-all ${errors.valorPago ? 'border-red-400 bg-red-50' : 'border-gray-200 focus:border-green-400 bg-gray-50'}`}
                />
              </div>
              {errors.valorPago && <p className="text-red-500 text-xs mt-1">{errors.valorPago}</p>}
            </div>

            {valorEstimado !== null && valorEstimado > 0 && (
              <div
                ref={resultRef}
                className="bg-green-50 border-2 border-green-300 rounded-2xl p-5 animate-pulse-once"
              >
                <p className="text-sm text-gray-500 mb-1 font-medium">Valor estimado de recuperação:</p>
                <p className="text-4xl font-extrabold text-green-600 mb-2">
                  R$ {valorEstimado.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </p>
                <div className="w-full bg-gray-200 rounded-full h-2.5 mb-2">
                  <div className="bg-green-500 h-2.5 rounded-full" style={{ width: '76%' }}></div>
                </div>
                <p className="text-xs text-gray-400">Baseado em 76% do valor pago informado</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full h-16 bg-green-500 hover:bg-green-600 disabled:bg-green-300 text-white font-bold text-base rounded-full transition-all duration-300 hover:scale-[1.02] cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
            >
              {loading ? (
                <><i className="ri-loader-4-line animate-spin"></i> Enviando...</>
              ) : (
                <><i className="ri-send-plane-line"></i> Quero recuperar meu dinheiro</>
              )}
            </button>

            <p className="text-center text-xs text-gray-400">
              <i className="ri-lock-line mr-1"></i>
              Seus dados estão seguros. Não compartilhamos com terceiros.
            </p>
          </form>
        )}
      </div>
    </section>
  );
}
